import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { Button, FormField, Input, useToast } from "@/ui";
import { ApiError, requestPasswordReset } from "@/api";
import { AuthShell } from "@/auth/AuthShell";
import { AuthAlert } from "@/auth/AuthAlert";
import { ChevronBackIcon, MailBigIcon } from "@/auth/icons";
import { useT } from "./i18n";

/**
 * ForgotPasswordPage — the "mot de passe oublié" surface, wired to the REAL public endpoint
 * POST /auth/password/reset-request (iam, publicRoute + authLimiter). Enumeration-safe BY CONTRACT:
 * the server returns the same ack whether or not the e-mail matches, so on success we ALWAYS show
 * the same neutral confirmation — never leaking account existence. A 429 surfaces a rate-limit
 * notice; any other transport failure surfaces a generic retry message (still no enumeration).
 */
export function ForgotPasswordPage() {
  const { t } = useT();
  const toast = useToast();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim() || submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      await requestPasswordReset(email.trim());
      setDone(true);
    } catch (err) {
      const message =
        err instanceof ApiError && err.httpStatus === 429
          ? t("auth.forgot.ratelimited")
          : t("auth.forgot.error");
      setError(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <AuthShell>
        <div className="nv-auth__confirm">
          <span className="nv-auth__confirm-badge" aria-hidden>
            <MailBigIcon />
          </span>
          <h1 className="nv-auth__h">{t("auth.forgot.done.title")}</h1>
          <p className="nv-auth__confirm-text" role="status">
            {t("auth.forgot.done")}
          </p>
        </div>
        <Link to="/login" className="nv-auth__back">
          <ChevronBackIcon />
          <span>{t("auth.forgot.back")}</span>
        </Link>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <h1 className="nv-auth__h">{t("auth.forgot.title")}</h1>
      <p className="nv-auth__sub">{t("auth.forgot.subtitle")}</p>

      {error && <AuthAlert tone="red">{error}</AuthAlert>}

      <form onSubmit={onSubmit} className="nv-auth__form" style={{ marginBlockStart: "var(--nv-s-4)" }}>
        <FormField label={t("auth.forgot.email")} required>
          {(p) => (
            <Input
              {...p}
              type="email"
              inputMode="email"
              autoComplete="username"
              placeholder={t("auth.login.email.placeholder")}
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          )}
        </FormField>

        <Button type="submit" block loading={submitting}>
          {submitting ? t("auth.forgot.submitting") : t("auth.forgot.submit")}
        </Button>
      </form>

      <Link to="/login" className="nv-auth__back">
        <ChevronBackIcon />
        <span>{t("auth.forgot.back")}</span>
      </Link>
    </AuthShell>
  );
}
